import * as React from 'react';
import cx from 'classnames';
import { capitalize } from 'lodash';
import { Tooltip } from 'antd';
import {
  AlignLeftIcon,
  AlignCenterIcon,
  AlignRightIcon,
  AlignJustifyIcon,
} from '@revfluence/fresh-icons/regular/esm';

import { IndentDecreaseIcon, IndentIncreaseIcon } from 'lucide-react';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/components/ui/popover';
import { TTextAlign, TIndentType } from './types';

const { useState } = React;

const ALIGNMENT_ICON: {
  [key in TTextAlign]: React.ReactNode;
} = {
  left: <AlignLeftIcon width={14} height={14} />,
  center: <AlignCenterIcon width={14} height={14} />,
  right: <AlignRightIcon width={14} height={14} />,
  justify: <AlignJustifyIcon width={14} height={14} />,
};

const ALIGNMENT_OPTIONS: TTextAlign[] = ['left', 'center', 'right'];

const INDENT_ICON: {
  [key in TIndentType]: React.ReactNode;
} = {
  indent: <IndentIncreaseIcon width={14} height={14} />,
  outdent: <IndentDecreaseIcon width={14} height={14} />,
};

const INDENT_OPTIONS: TIndentType[] = ['outdent', 'indent'];

const INDENT_LABEL: {
  [key in TIndentType]: string;
} = {
  indent: 'Increase Indent',
  outdent: 'Decrease Indent',
};

interface IProps {
  currentAlignment?: TTextAlign;
  onAlignmentChange(alignment: TTextAlign): void;
  onIndentChange(type: TIndentType): void;
  className?: string;
}

export const AlignmentControls: React.FC<IProps> = React.memo((props) => {
  const { currentAlignment = 'left' } = props;
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const allOptions = [
    ...ALIGNMENT_OPTIONS.map((option) => ({ type: 'alignment', value: option })),
    ...INDENT_OPTIONS.map((option) => ({ type: 'indent', value: option }))
  ];

  return (
    <Popover open={dropdownVisible} onOpenChange={setDropdownVisible} modal>
      <PopoverTrigger asChild>
        <div
          className={cx(
            'flex items-center justify-center w-6 h-6 rounded cursor-pointer transition-colors duration-150 hover:bg-grey-2',
            {
              'bg-grey-1': dropdownVisible,
            },
            props.className
          )}
          onMouseDown={(event) => event.preventDefault()}
        >
          <Tooltip title="Formatting">
            <FontAwesomeIcon icon={faEllipsis} className="h-3 w-3 text-primary cursor-pointer" />
          </Tooltip>
        </div>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-2 z-[9999] rounded-2xl">
        <div className="flex flex-col gap-1">
          {allOptions.map(({ type, value }) => (
            <div
              key={`${type}-${value}`}
              className={cx(
                'px-2 py-2 cursor-pointer hover:bg-grey-1 flex items-center gap-3 transition-colors duration-150 rounded',
                {
                  'bg-blue-1 text-blue-5': type === 'alignment' && currentAlignment === value,
                }
              )}
              onMouseDown={(event) => {
                event.preventDefault();
                if (type === 'alignment') {
                  props.onAlignmentChange(value as TTextAlign);
                } else {
                  props.onIndentChange(value as TIndentType);
                }
                setDropdownVisible(false);
              }}
            >
              <div className="flex items-center justify-center w-5 h-5">
                {type === 'alignment' ? ALIGNMENT_ICON[value as TTextAlign] : INDENT_ICON[value as TIndentType]}
              </div>
              <span className="font-medium flex-1 text-grey-6">
                {type === 'alignment' ? `Align ${capitalize(value)}` : INDENT_LABEL[value as TIndentType]}
              </span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
});

AlignmentControls.displayName = 'AlignmentControls';
